"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.loadScriptWithFallbacks = void 0;
const replaceServerInUrl_1 = require("./replaceServerInUrl");
/**
 * Loads a script from the given URL, with the given callbacks. If the script fails to load, it will try to load it
 * from the next fallback server, and so on until it runs out of fallback servers.
 *
 * @param url The URL to load the script from
 * @param fallbackServers The fallback servers to try loading the script from if the initial URL fails
 * @param callbacks The callbacks to call when the script is loaded, fails to load, or falls back to a fallback server
 * @returns A promise that resolves when the script is loaded, or rejects if it fails to load from all fallback servers
 *
 * @example
 * ```
 * loadScriptWithFallbacks("http://foo/legal/remoteEntry.js", ["https://bar:50", "https://baz:50"], {
 *   onLoaded: () => console.log("Loaded!"),
 *   onError: () => console.log("Failed to load!"),
 *   onFallback: () => console.log("Fell back to a fallback server!"),
 * }).catch(() => console.log("Failed to load from all fallback servers!"));
 * ```
 */
async function loadScriptWithFallbacks(url, fallbackServers, callbacks) {
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        function removeScript() {
            script.remove();
        }
        script.src = url;
        script.type = "application/javascript";
        script.async = true;
        script.crossOrigin = "anonymous";
        script.setAttribute("data-testid", "unique-id"); //for testing purposes
        script.onload = () => {
            script.onload = null;
            script.onerror = null;
            resolve({
                loadedUrl: url,
            });
        };
        script.onerror = () => {
            script.onload = null;
            script.onerror = null;
            callbacks.onError();
            removeScript();
            if (fallbackServers.length > 0 && !callbacks.shouldCancel()) {
                const fallbackServer = fallbackServers.shift();
                const urlWithFallback = (0, replaceServerInUrl_1.replaceServerInUrl)(url, fallbackServer);
                callbacks.onFallback();
                loadScriptWithFallbacks(urlWithFallback, fallbackServers, callbacks).then(resolve, reject);
            }
            else {
                reject();
            }
        };
        document.head.appendChild(script);
    });
}
exports.loadScriptWithFallbacks = loadScriptWithFallbacks;
